import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plane, Globe, Users, BookOpen, HeartPulse, Cpu } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cleanCourseList, buildPopularCards, getCourseImage, getDisplayTitle } from '../../lib/landingCourses';
import { localeUpperCase } from '../../lib/textCase';
import { getStudentCount } from '../../lib/studentCount';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_GRADIENT = {
  cabin_crew: 'from-[#0E1F3D] to-[#1c3560]',
  language: 'from-[#4d90bd] to-[#7FB6D9]',
  business: 'from-[#a8861a] to-[#C9A227]',
  hr_training: 'from-[#a8861a] to-[#C9A227]',
  // landing-populer-ve-sayac: 17/32 prod kursu `health` — önceden bu haritada
  // yoktu, varsayılan lacivert gradyana düşüyordu. Yeşilimsi ton, mevcut
  // lacivert/altın paletle çakışmıyor.
  health: 'from-[#0d3b33] to-[#1a5c4d]',
  // aiLiteracy küratörlü kartı `programming` kategorisinde.
  programming: 'from-[#241c02] to-[#4a3a08]',
};

const CATEGORY_ICON = {
  cabin_crew: Plane,
  language: Globe,
  business: Users,
  hr_training: Users,
  health: HeartPulse,
  programming: Cpu,
};

// landing-populer-ve-sayac: rozet metni önceden 3'lü ternary'ydi (cabin_crew /
// language / DİĞER HEPSİ → "Kariyer") — prod'daki 17 `health` kursu yanlışlıkla
// "Kariyer" rozeti alıyordu. Açık haritaya çevrildi; bilinmeyen kategori
// sessizce "Kariyer" olmasın diye ayrı bir "general" fallback key'i var.
const CATEGORY_BADGE_KEY = {
  cabin_crew: 'landing2.trending.tab.aviation',
  language: 'landing2.trending.tab.languages',
  business: 'landing2.trending.tab.career',
  hr_training: 'landing2.trending.tab.career',
  health: 'landing2.trending.tab.health',
  programming: 'landing2.trending.tab.tech',
};

export default function PopularCourses() {
  const { t, lang, getField } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [cards, setCards] = useState([]);
  const rowRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // /courses/featured backend'de limit=8 sabit; demo/test filtresi bu limit'ten
    // SONRA uygulanirsa gercek kurslar hic cekilmeden atilabilir. Bunun yerine
    // /courses?sort=popular (ayni enrolled_count-desc siralama) genis bir limit'le
    // (30) cekilir, temizlenir, sonra gosterilecek sayiya (8) indirilir.
    axios.get(`${API}/courses?sort=popular&limit=30`)
      .then((res) => {
        if (cancelled) return;
        // landing-populer-ve-sayac: kategori ÖNCELİĞİ yerine ÇEŞİTLİLİĞİ —
        // interleaveByCategory ile round-robin 8 gerçek kurs alınır, iki
        // sabit indekse (2, 6) küratörlü kart (Yer Hostesliği / Yapay Zekâ)
        // yerleştirilir. bkz. buildPopularCards.
        const built = buildPopularCards(cleanCourseList(res.data?.courses || []), 10);
        setCards(built);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  const scroll = (dir) => rowRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });

  return (
    <section id="populer" className="bg-gradient-to-b from-[#0B1A33] to-[#0d213f] py-16 md:py-20" data-testid="landing2-popular">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between mb-6 gap-4">
          <h2 className="landing2-display text-2xl md:text-[32px] text-[#F7F5F0]">
            {localeUpperCase(t('landing2.popular.title'), lang)}{' '}
            <Link to="/courses" className="text-[15px] font-semibold text-[#7FB6D9] ml-2 hover:text-[#a9d0e8]">
              {t('landing2.popular.viewAll')}
            </Link>
          </h2>
          {cards.length > 0 && (
            <div className="hidden md:flex gap-3">
              <button
                type="button"
                onClick={() => scroll(-1)}
                className="w-11 h-11 rounded-full border border-white/28 text-[#F7F5F0] flex items-center justify-center hover:bg-[#C9A227] hover:text-[#241c02] hover:border-[#C9A227] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C9A227] transition-colors"
                aria-label={lang === 'tr' ? 'Önceki' : 'Previous'}
              >
                <ChevronLeft size={20} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scroll(1)}
                className="w-11 h-11 rounded-full border border-white/28 text-[#F7F5F0] flex items-center justify-center hover:bg-[#C9A227] hover:text-[#241c02] hover:border-[#C9A227] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C9A227] transition-colors"
                aria-label={lang === 'tr' ? 'Sonraki' : 'Next'}
              >
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {status === 'loading' && (
          <div className="flex gap-5 overflow-hidden">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex-shrink-0 w-[262px] aspect-[3/4.1] rounded-2xl bg-white/10 animate-pulse" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <p className="text-[#c5cfdd] font-semibold py-6">{t('landing2.common.error')}</p>
        )}

        {status === 'ready' && cards.length === 0 && (
          <p className="text-[#c5cfdd] font-semibold py-6">{t('landing2.popular.empty')}</p>
        )}

        {status === 'ready' && cards.length > 0 && (
          <div ref={rowRef} className="flex gap-5 overflow-x-auto no-scrollbar scroll-smooth pb-3 pt-1.5" data-testid="popular-row">
            {cards.map((item) => {
              const isCurated = item.type === 'curated';
              const c = isCurated ? item.card : item.course;
              const category = c.category;
              const Icon = CATEGORY_ICON[category] || BookOpen;
              const gradient = CATEGORY_GRADIENT[category] || 'from-[#0E1F3D] to-[#1c3560]';
              const badgeKey = CATEGORY_BADGE_KEY[category] || 'landing2.trending.tab.general';
              // health kategorisinde stok fotoğrafımız yok (bkz. landingCourses.js
              // CATEGORY_IMAGE_POOL notu) — DEFAULT_COURSE_IMAGE (uçak fotoğrafı)
              // ile 17 sağlık kursunu göstermek yerine hiç <img> render edilmez,
              // sadece gradyan + büyük ikon görünür.
              const showImage = category !== 'health';
              const key = isCurated ? c.key : c.course_id;
              const to = isCurated ? c.to : `/courses/${c.course_id}`;
              const title = isCurated ? t(c.titleKey) : getDisplayTitle(c, lang, getField);
              return (
                <Link
                  to={to}
                  key={key}
                  className={`poster-card flex-shrink-0 w-[262px] aspect-[3/4.1] rounded-2xl overflow-hidden relative bg-gradient-to-br ${gradient} shadow-[0_14px_34px_rgba(0,0,0,.4)] hover:-translate-y-2 hover:scale-[1.015] transition-transform`}
                  data-testid="popular-course-card"
                >
                  {showImage && (
                    <img
                      src={isCurated ? c.image : getCourseImage(c)}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <Icon
                    size={showImage ? 40 : 72}
                    strokeWidth={1.6}
                    className={showImage
                      ? 'absolute top-4 right-4 text-white/70 drop-shadow'
                      : 'absolute inset-0 m-auto text-white/25'}
                    aria-hidden="true"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/90" aria-hidden="true" />
                  <span className="absolute top-3.5 left-3.5 bg-[#34CC67] text-[#08331d] text-[11px] font-extrabold px-2.5 py-1 rounded-full">
                    {t(badgeKey)}
                  </span>
                  <div className="absolute left-0 right-0 bottom-0 p-[18px]">
                    <h3 className="landing2-display text-[22px] text-white drop-shadow-md">
                      {localeUpperCase(title, lang)}
                    </h3>
                    <div className="w-[26px] h-[3px] bg-[#C9A227] my-2.5" />
                    <div className="text-[13px] font-semibold text-[#d7deea]">
                      {isCurated
                        ? t('landing2.popular.curatedSubtitle')
                        : `${c.lessons_count || 0} ${t('landing2.common.lessonsSuffix')} · ${getStudentCount(c)} ${t('landing2.common.studentsSuffix')}`}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
